import { memo } from 'react';

import { VFlex, VStack, getStringToRGBA } from '@/package';
import type { Task, UserTask } from '@/type';

const TaskMemberProgressItem = ({ userTask }: { userTask: UserTask }) => {
  const progress = +(
    (userTask.completedFrameCount / userTask.totalFrameCount) *
    100
  ).toFixed(2);
  const leftFrameCount =
    userTask.totalFrameCount - userTask.completedFrameCount;

  return (
    <VStack
      className="max-w-[182px] min-w-[182px] rounded-md border border-gray-200 px-scale/16 py-scale/8 text-body-md text-gray-500"
      data-testid={`task-member-progress-item-${userTask.userID}`}
    >
      <VFlex
        items="center"
        data-testid="task-member-progress-item-user-name"
        gap={0.25}
      >
        <div
          className="inline-block size-2 rounded-full bg-(--bg-color)"
          style={
            {
              '--bg-color': getStringToRGBA(userTask.userID),
            } as React.CSSProperties
          }
        ></div>
        <div>{userTask.user.name || userTask.user.email.split('@')[0]}</div>
      </VFlex>

      <VFlex
        items="center"
        gap={0.25}
        data-testid="task-member-progress-item-progress"
      >
        <div className="relative h-2 flex-1 rounded-full bg-gray-300">
          <div
            className="absolute top-0 left-0 h-full w-full rounded-full bg-status-completed"
            style={{
              width: `${progress}%`,
            }}
          ></div>
        </div>

        <div className="w-9 text-right text-body-sm text-status-completedEnd">
          {progress}%
        </div>
      </VFlex>

      <VFlex>Left frames : {leftFrameCount}</VFlex>
    </VStack>
  );
};

TaskMemberProgressItem.displayName = 'TaskMemberProgressItem';

export const TaskMemberProgressList = memo(
  ({ taskDetail }: { taskDetail: Task }) => {
    const userTask = taskDetail.userTask ?? [];

    return (
      <VStack gap={0.5}>
        <div>
          <strong className="text-body-xl text-secondary-500">
            Member progress
          </strong>
        </div>

        <VFlex className="overflow-x-auto overflow-y-hidden" gap={0.5}>
          {userTask.map(userTask => (
            <TaskMemberProgressItem key={userTask.userID} userTask={userTask} />
          ))}
        </VFlex>
      </VStack>
    );
  },
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.taskDetail?.userTask) ===
      JSON.stringify(nextProps.taskDetail?.userTask)
    );
  },
);

TaskMemberProgressList.displayName = 'TaskMemberProgressList';
